/**
 * Broadcast list — `GET messaging/broadcasts/`. `BroadcastMessageViewSet`
 * gates EVERY method (including GET) to manager roles server-side — a
 * TENANT gets 403 even trying to list broadcasts they might be a target of.
 * This page is therefore only ever mounted under the landlord/agent role
 * trees (see router.tsx) — there is no tenant-facing broadcast inbox.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { deleteBroadcast, listBroadcasts } from '../../api/endpoints/messaging';

export function BroadcastListPage(): React.JSX.Element {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['broadcasts', { page }],
    queryFn: () => listBroadcasts({ page }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteBroadcast(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['broadcasts'] }),
  });

  const broadcasts = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  return (
    <section>
      <h2>Broadcasts</h2>

      <p>
        <Link to="new">New broadcast</Link>
      </p>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && broadcasts.length === 0 && <p>No broadcasts sent yet.</p>}

      {query.isSuccess && broadcasts.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {broadcasts.map((broadcast) => (
              <li key={broadcast.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Message</span>
                  <span className="record-card__value">{broadcast.message}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Target group</span>
                  <span className="record-card__value">{broadcast.target_group}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Sent by</span>
                  <span className="record-card__value">{broadcast.sender_name}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Sent</span>
                  <span className="record-card__value">{broadcast.created_at}</span>
                </div>
                <div className="record-card__actions">
                  <Link to={`${broadcast.id}/edit`} className="button button--outlined">
                    <Pencil size={14} className="icon-inline" aria-hidden="true" />
                    Edit
                  </Link>
                  <button
                    type="button"
                    className="button--danger"
                    disabled={remove.isPending}
                    onClick={() => setConfirmDeleteId(broadcast.id)}
                  >
                    <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Message</th>
                <th>Target group</th>
                <th>Sent by</th>
                <th>Sent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((broadcast) => (
                <tr key={broadcast.id}>
                  <td>{broadcast.message}</td>
                  <td>{broadcast.target_group}</td>
                  <td>{broadcast.sender_name}</td>
                  <td>{broadcast.created_at}</td>
                  <td>
                    <span className="row-actions">
                      <Link to={`${broadcast.id}/edit`}>
                        <Pencil size={14} className="icon-inline" aria-hidden="true" />
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="button--danger"
                        disabled={remove.isPending}
                        onClick={() => setConfirmDeleteId(broadcast.id)}
                      >
                        <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete broadcast"
        message="Delete this broadcast? This cannot be undone."
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDeleteId !== null) remove.mutate(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </section>
  );
}

export default BroadcastListPage;
